import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { FdaRegistrationListingModel } from '../../../../models/data-sections/fda/registrationlistingModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface RegistrationListingModalProps {
    searchExecutionId: string;
    selected_registration: FdaRegistrationListingModel;
}

const RegistrationListingModal: React.FC<RegistrationListingModalProps> = ({
    searchExecutionId,
    selected_registration,
}) => {
    const [data, setData] = useState<FdaRegistrationListingModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didErrorOccur, setDidErrorOccur] = useState(false);
    const { triggerSystemError } = useSystemError();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await fetchDetails(searchExecutionId, selected_registration.key_proprietary_name, selected_registration.key_product_code ?? '', selected_registration.key_registration_number, selected_registration.key_k_number, selected_registration.key_pma_number, new Date(selected_registration.__ci_system_search_date_key ?? 0));
            } catch (error) {
                console.error('Error during fetch:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [searchExecutionId, selected_registration]);

    const fetchDetails = async (search_execution_id: string, key_proprietary_name: string, key_product_code: string, key_registration_number: string, key_k_number: string, key_pma_number: string, __ci_system_search_date_key: Date): Promise<void> => {
        try {
            const response = await fdaApi.getRegistrationListingsDetail(search_execution_id, key_proprietary_name, key_product_code, key_registration_number, key_k_number, key_pma_number, __ci_system_search_date_key);

            if (response.success) {
                const data = (response as ResponseSuccessModel).data as FdaRegistrationListingModel[];
                setData(data?.[0] || null);
            } else {
                handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
            }
        } catch (error) {
            handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Typography variant="h6">
                    {data?.key_proprietary_name || 'Registration Record'}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                    {selected_registration.key_registration_number} / {selected_registration.key_product_code}
                </Typography>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            ) : didErrorOccur || !data ? (
                <Typography color="error">Failed to load registration details.</Typography>
            ) : (
                <Box mt={2}>
                    <Typography><strong>Proprietary Name:</strong> {data.key_proprietary_name || 'N/A'}</Typography>
                    <Typography><strong>Product Code:</strong> {data.key_product_code || 'N/A'}</Typography>
                    <Typography><strong>Registration Number:</strong> {data.key_registration_number || 'N/A'}</Typography>
                    <Typography><strong>K Number:</strong> {data.key_k_number || 'N/A'}</Typography>
                    <Typography><strong>PMA Number:</strong> {data.key_pma_number || 'N/A'}</Typography>
                    <Typography><strong>Created Date:</strong> {data.product_created_date || 'N/A'}</Typography>

                    {Array.isArray(data.establishment_type) && data.establishment_type.length > 0 && (
                        <Typography><strong>Establishment Type:</strong> {data.establishment_type.join(', ')}</Typography>
                    )}

                    {data.product?.openfda && (
                        <Box mt={3}>
                            <Typography variant="h6">OpenFDA Details</Typography>
                            <Typography><strong>Device Name:</strong> {data.product.openfda.device_name || 'N/A'}</Typography>
                            <Typography><strong>Specialty:</strong> {data.product.openfda.medical_specialty_description || 'N/A'}</Typography>
                            <Typography><strong>Regulation:</strong> {data.product.openfda.regulation_number || 'N/A'}</Typography>
                            <Typography><strong>Device Class:</strong> {data.product.openfda.device_class || 'N/A'}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default RegistrationListingModal;
